/** 手机使用要求：记录按字段阅读，金额/薪资对比保留横向表；弹窗表单可换行、主要操作便于触控。只调整显示，不改变数据、权限及审批规则。 */
import React, { useEffect, useState, useCallback } from "react";
import { useApp } from "../../context/AppContext";
import { Link } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { toast } from "../../components/ui/sonner";
import { Loader2, Wallet, Gift, Target, CalendarClock, GraduationCap, FolderKanban, Palmtree, LogIn, LogOut, CheckCircle2, Megaphone, CalendarDays, ExternalLink, Bell } from "lucide-react";
import { LEAVE_TYPES, LEAVE_TYPES_EN, DEPTS, DEPTS_EN, inp, getGeo } from "./hrmsShared";
import { CnMyPay } from "./cn/CnMyPay";
import { MyDevices } from "../../components/hrms/MyDevices";
import { PersonnelBusinessInbox } from "../../components/hrms/PersonnelBusinessInbox";
import { UsPayCycleCard, UsMyPayslips, useUsMyPay } from "./us/UsMyPay";
import { fmtD } from "../../lib/date";

const fmtM = (n, cur) => ((cur === "CNY" ? "¥" : "$") + Math.round(n || 0).toLocaleString());
const GRADE_CLS = { A: "bg-emerald-100 text-emerald-700", B: "bg-sky-100 text-sky-700", C: "bg-amber-100 text-amber-700", D: "bg-red-100 text-red-600" };
const localDate = () => { const n = new Date(); return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, "0")}-${String(n.getDate()).padStart(2, "0")}`; };
const localTime = () => { const n = new Date(); return `${String(n.getHours()).padStart(2, "0")}:${String(n.getMinutes()).padStart(2, "0")}`; };

export default function HRMSMyHR() {
  const { api, lang } = useApp();
  const zh = lang === "zh";
  const T = (z, e) => (zh ? z : e);
  const [data, setData] = useState(null);
  const [today, setToday] = useState(null);
  // 首页公告栏 + HR 日历（对应 HR 反馈「公告放全员首页」「HR 日历」）
  const [news, setNews] = useState([]);
  const [cal, setCal] = useState([]);
  const [notifDlg, setNotifDlg] = useState(null); // 通知相关性过滤设置
  const [leaveDlg, setLeaveDlg] = useState(null);
  const [busy, setBusy] = useState(false);

  const reload = useCallback(() => {
    api.get("/hrms/me").then(({ data }) => setData(data)).catch(() => setData({ linked: false }));
    api.get("/hrms/attendance/today", { params: { localDate: localDate() } }).then(({ data }) => setToday(data)).catch(() => {});
    api.get("/hrms/newsletter", { params: { limit: 5 } }).then(({ data }) => setNews(data.rows || [])).catch(() => setNews([]));
    api.get("/hrms/calendar", { params: { month: new Date().toISOString().slice(0, 7) } }).then(({ data }) => setCal(data.rows || [])).catch(() => setCal([]));
  }, [api]);
  useEffect(() => { reload(); }, [reload]);

  const clock = async (action) => {
    setBusy(true);
    try {
      const geo = await getGeo();
      const { data } = await api.post("/hrms/attendance/clock", { action, localDate: localDate(), localTime: localTime(), ...(geo || {}) });
      toast.success(action === "in" ? T(`上班打卡成功 ${localTime()}`, `Clocked in ${localTime()}`) : T(`下班打卡成功 ${localTime()}，辛苦了！`, `Clocked out ${localTime()} — great work!`));
      if (data?.site?.address) toast.success(T(`📍 已匹配工地：${data.site.address}`, `📍 Site matched: ${data.site.address}`));
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || T("打卡失败", "Clock failed")); }
    setBusy(false);
  };
  const submitLeave = async () => {
    if (!leaveDlg.startDate || !leaveDlg.endDate) return toast.error(T("请选择起止日期", "Select start and end dates"));
    setBusy(true);
    try {
      await api.post("/hrms/leaves", leaveDlg);
      toast.success(T("请假申请已提交，等待审批", "Leave request submitted for approval"));
      setLeaveDlg(null); reload();
    } catch (e) { toast.error(e?.response?.data?.detail || T("提交失败", "Submit failed")); }
    setBusy(false);
  };
  const [slipMonth, setSlipMonth] = useState(new Date().toISOString().slice(0, 7));
  const usPay = useUsMyPay(api, !!data?.linked && (data.salary?.currency || "USD") !== "CNY");
  // 通知相关性过滤：读取/保存用户的「关注部门 + 关注类型」
  const openNotif = async () => {
    try { const { data } = await api.get("/hrms/notif-prefs"); setNotifDlg(data); }
    catch { toast.error(T("加载失败", "Load failed")); }
  };
  const saveNotif = async () => {
    if (!notifDlg) return;
    setBusy(true);
    try { await api.put("/hrms/notif-prefs", notifDlg.prefs); toast.success(T("通知偏好已保存", "Notification preferences saved")); setNotifDlg(null); }
    catch (e) { toast.error(e?.response?.data?.detail || T("保存失败", "Save failed")); }
    setBusy(false);
  };
  const downloadSlip = async () => {
    try {
      const res = await api.get("/hrms/payroll/payslip", { params: { employeeId: data.profile.id, month: slipMonth }, responseType: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(res.data);
      a.download = zh ? `工资单_${slipMonth}.pdf` : `payslip_${slipMonth}.pdf`; a.click();
      URL.revokeObjectURL(a.href);
      toast.success(T(`${slipMonth} 工资单已下载`, `${slipMonth} payslip downloaded`));
    } catch (err) {
      try { toast.error(JSON.parse(await err.response.data.text()).detail || T("下载失败", "Download failed")); }
      catch { toast.error(T("下载失败", "Download failed")); }
    }
  };

  if (!data) return <div className="p-8 text-center text-slate-400"><Loader2 className="h-5 w-5 animate-spin inline" /></div>;
  if (!data.linked) return <div className="p-10 text-center text-slate-400 text-sm" data-testid="hrms-me-unlinked">{T("您的账号尚未关联员工档案，请联系 HR 建档", "Your account is not linked to an employee record — please contact HR")}</div>;

  const p = data.profile;
  const b = data.benefits;
  const inc = data.incentives;
  const cur = data.salary?.currency || "USD";
  const incTotal = inc.commission + inc.pmBonus + inc.designPiece + inc.otPay;
  const card = "rounded-xl border border-slate-200 bg-white p-4";
  const row = "flex justify-between text-xs text-slate-500 py-1";

  return (
    <div className="space-y-4" data-testid="hrms-me-page">
      <PersonnelBusinessInbox />
      <div className="rounded-xl bg-slate-900 text-white p-5 flex flex-wrap items-center gap-4" data-testid="hrms-me-profile">
        <div className="h-12 w-12 rounded-full bg-brand-500 flex items-center justify-center text-lg font-extrabold">{(p.name || "?")[0]}</div>
        <div className="flex-1 min-w-0">
          <h1 className="text-lg font-extrabold" data-keep-title>{p.name}{p.nameZh ? ` · ${p.nameZh}` : ""}</h1>
          {/* 岗位显示（2026-10-26）：优先用后端下发的 roleLabel —— 自定义岗位（如「招聘专员」）
              的档案 role 只是它继承的基础角色，直接渲染会把岗位显示成「工程部」，
              让本人误以为自己是施工岗。取不到时退回 role 原值，内置岗位表现不变。 */}
          <p className="text-xs text-slate-400 mt-0.5">{(zh ? DEPTS : DEPTS_EN)[p.department] || p.department} · {zh ? (p.roleLabel || p.role) : (p.roleLabelEn || p.roleLabel || p.role)} · {T("入职", "Hired")} {fmtD(p.hireDate) || "-"} · {data.period}</p>
        </div>
        <div className="text-right space-y-1.5">
          {data.perf && <span className={`text-sm px-3 py-1 rounded-full font-extrabold ${GRADE_CLS[data.perf.finalGrade] || "bg-slate-700"}`} data-testid="hrms-me-grade">{T("本期绩效", "Performance")} {data.perf.finalGrade} · {data.perf.composite} {T("分", "pts")}</span>}
          <div><button data-testid="hrms-me-notif-btn" onClick={openNotif} className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 inline-flex items-center gap-1 text-[11px] rounded-full border border-white/30 text-white/90 hover:bg-white/10 font-semibold px-3 py-1"><Bell className="h-3 w-3" />{T("通知设置", "Notifications")}</button></div>
        </div>
      </div>

      {/* 全员首页公告栏（对应 HR 反馈：公告应出现在每个人登录后的首页） */}
      {news.length > 0 && (
        <div className="rounded-xl border border-amber-200 bg-amber-50/50 p-4" data-testid="hrms-me-news">
          <p className="text-sm font-bold text-amber-800 flex items-center gap-1.5 mb-2"><Megaphone className="h-4 w-4" />{T("公司公告", "Announcements")}</p>
          <div className="space-y-1.5">
            {news.map((n) => (
              <div key={n.id} className="rounded-lg bg-white border border-amber-100 px-3 py-2">
                <div className="flex items-center gap-2">
                  {n.pinned && <span className="text-[9px] px-1.5 py-0.5 rounded bg-amber-200 text-amber-800 font-bold">{T("置顶", "PIN")}</span>}
                  <p className="text-xs font-bold text-slate-700 flex-1">{n.title}</p>
                  <span className="text-[10px] text-slate-400">{fmtD(n.createdAt)} · {n.by}</span>
                </div>
                {n.content && <p className="text-[11px] text-slate-500 mt-0.5 whitespace-pre-wrap">{n.content}</p>}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* 本月 HR 日历（评估 / 培训 / 生日 / 内部提醒） */}
      {cal.length > 0 && (
        <div className="rounded-xl border border-sky-200 bg-sky-50/50 p-4" data-testid="hrms-me-cal">
          <p className="text-sm font-bold text-sky-800 flex items-center gap-1.5 mb-2"><CalendarDays className="h-4 w-4" />{T("本月日程", "This Month's Schedule")}</p>
          <div className="space-y-1">
            {cal.map((ev) => (
              <div key={ev.id} className="flex items-center gap-2 text-xs">
                <span className="text-slate-500 w-24 shrink-0">{fmtD(ev.date)}{ev.time ? ` ${ev.time}` : ""}</span>
                <span className="font-semibold text-slate-700 flex-1 truncate">{ev.title}</span>
                <a href={ev.googleUrl} target="_blank" rel="noreferrer" className="text-slate-400 hover:text-sky-600" title={T("加入 Google 日历", "Add to Google Calendar")}><ExternalLink className="h-3.5 w-3.5" /></a>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 gap-3" data-testid="hrms-me-quick">
        <div className="rounded-xl border border-slate-200 bg-white p-4 flex flex-col items-center justify-center gap-2">
          {!today?.clockIn ? (
            <button data-testid="hrms-me-clock-in" disabled={busy} onClick={() => clock("in")}
              className="w-full h-14 rounded-xl bg-brand-grad hover:opacity-90 text-white shadow-brand font-extrabold text-base flex items-center justify-center gap-2 disabled:opacity-50">
              <LogIn className="h-5 w-5" />{T("上班打卡", "Clock In")}
            </button>
          ) : !today?.clockOut ? (
            <>
              <p className="text-[11px] text-emerald-600 font-semibold">{today.clockIn} {T("已上班", "clocked in")}</p>
              <button data-testid="hrms-me-clock-out" disabled={busy} onClick={() => clock("out")}
                className="w-full h-12 rounded-xl border-2 border-slate-300 text-slate-600 hover:border-brand-300 hover:text-brand-600 font-extrabold text-base flex items-center justify-center gap-2 disabled:opacity-50">
                <LogOut className="h-5 w-5" />{T("下班打卡", "Clock Out")}
              </button>
            </>
          ) : (
            <p data-testid="hrms-me-clock-done" className="text-sm font-bold text-emerald-600 flex items-center gap-1.5"><CheckCircle2 className="h-5 w-5" />{today.clockIn}–{today.clockOut} {T("已打卡", "done")}</p>
          )}
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-4 flex items-center justify-center">
          <button data-testid="hrms-me-leave-btn" onClick={() => setLeaveDlg({ type: "annual", startDate: localDate(), endDate: localDate(), days: 1, reason: "" })}
            className="w-full h-14 rounded-xl bg-slate-800 hover:bg-slate-700 text-white font-extrabold text-base flex items-center justify-center gap-2">
            <Palmtree className="h-5 w-5" />{T("快捷请假", "Quick Leave")}
          </button>
        </div>
      </div>

      {cur !== "CNY" && usPay?.cycle && (
        <div className="grid md:grid-cols-3 gap-3" data-testid="hrms-me-us-pay-row">
          <UsPayCycleCard cycle={usPay.cycle} zh={zh} card={card} />
          <UsMyPayslips api={api} items={usPay.items || []} zh={zh} card={card} />
        </div>
      )}

      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-3">
        <div className={card} data-testid="hrms-me-pay">
          <p className="text-xs font-bold text-slate-600 flex items-center gap-1.5"><Wallet className="h-3.5 w-3.5 text-emerald-600" />{T("本月工资概览", "Pay This Month")}</p>
          {cur === "CNY" ? <CnMyPay zh={zh} /> : <div className="mt-2">
            <div className={row}><span>{data.salary.payType === "hourly" ? T("时薪", "Hourly") : T("月薪", "Monthly")}</span><b className="text-slate-700">{data.salary.payType === "hourly" ? fmtM(data.salary.hourlyRate, cur) + "/h" : fmtM(data.salary.baseSalary, cur)}</b></div>
            {inc.commission > 0 && <div className={row}><span>{T("销售提成", "Commission")}</span><b className="text-emerald-600">{fmtM(inc.commission, cur)}</b></div>}
            {inc.pmBonus > 0 && <div className={row}><span>{T("项目奖金（应计）", "PM bonus (accrued)")}</span><b className="text-emerald-600">{fmtM(inc.pmBonus, cur)}</b></div>}
            {inc.designPiece > 0 && <div className={row}><span>{T("设计计件", "Design piece rate")}</span><b className="text-emerald-600">{fmtM(inc.designPiece, cur)}</b></div>}
            {inc.otPay > 0 && <div className={row}><span>{T("加班工资", "Overtime pay")}</span><b className="text-emerald-600">{fmtM(inc.otPay, cur)}</b></div>}
            <div className="flex-wrap md:flex-nowrap border-t border-slate-100 mt-1 pt-1.5 flex justify-between text-xs"><span className="text-slate-500">{T("当期激励小计", "Incentives subtotal")}</span><b className="text-slate-800" data-testid="hrms-me-inc-total">{fmtM(incTotal, cur)}</b></div>
            {!data.salary.baseSalary && !data.salary.hourlyRate && <p className="text-[10px] text-amber-500 mt-1">{T("底薪未设置，请联系 HR 完善薪酬档案", "Base pay not set — please contact HR")}</p>}
            {(data.salary.baseSalary > 0 || data.salary.hourlyRate > 0) && (
              <div className="flex-wrap md:flex-nowrap mt-2 flex items-center gap-1.5">
                <input data-testid="hrms-me-slip-month" type="month" className="text-[11px] rounded border border-slate-200 px-1.5 py-1 flex-1"
                  value={slipMonth} onChange={(e) => setSlipMonth(e.target.value)} />
                <button data-testid="hrms-me-slip-btn" onClick={downloadSlip}
                  className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 text-[11px] rounded-full bg-emerald-600 hover:bg-emerald-700 text-white font-semibold px-3 py-1">{T("工资单 PDF", "Payslip PDF")}</button>
              </div>
            )}
          </div>}
        </div>

        <div className={card} data-testid="hrms-me-benefits">
          <p className="text-xs font-bold text-slate-600 flex items-center gap-1.5"><Gift className="h-3.5 w-3.5 text-violet-500" />{T("我的福利", "My Benefits")}</p>
          <div className="mt-2">
            <div className={row}><span>{T("医保计划", "Medical plan")}</span><b className={b.medicalPlan ? "text-slate-700" : "text-slate-300"}>{b.medicalPlanName}</b></div>
            <div className={row}><span>{T("401k 缴纳 / 公司匹配", "401k / employer match")}</span><b className="text-slate-700">{b.k401Pct || 0}% / {b.k401MatchPct || 0}%</b></div>
            <div className={row}><span>{T("年假 PTO 余额", "PTO balance")}</span><b className={b.ptoBalance < 0 ? "text-red-600" : "text-emerald-600"} data-testid="hrms-me-pto">{b.ptoBalance} / {data.config.annualLeaveDays} {T("天", "days")}</b></div>
            <div className={row}><span>{T("病假余额", "Sick balance")}</span><b className="text-slate-700">{b.sickBalance} / {data.config.sickDays} {T("天", "days")}</b></div>
          </div>
        </div>

        <div className={card} data-testid="hrms-me-attendance">
          <p className="text-xs font-bold text-slate-600 flex items-center gap-1.5"><CalendarClock className="h-3.5 w-3.5 text-sky-600" />{T("本月考勤", "Attendance This Month")}</p>
          <div className="mt-2">
            <div className={row}><span>{T("出勤天数 / 工时", "Days / hours")}</span><b className="text-slate-700">{data.attendance.days} {T("天", "d")} / {data.attendance.hours}h</b></div>
            <div className={row}><span>{T("迟到 / 早退", "Late / early-out")}</span><b className={data.attendance.late || data.attendance.early ? "text-amber-600" : "text-slate-700"}>{data.attendance.late} / {data.attendance.early}</b></div>
            <div className={row}><span>{T("加班 1.5× / 2×", "OT 1.5× / 2×")}</span><b className="text-slate-700">{data.attendance.ot15}h / {data.attendance.ot20}h</b></div>
            <Link to="/hrms/attendance" data-testid="hrms-me-att-link" className="inline-block mt-1 text-[11px] text-indigo-600 font-semibold hover:underline">{T("去打卡 / 查看考勤 →", "Clock / view attendance →")}</Link>
          </div>
        </div>

        <div className={card} data-testid="hrms-me-perf">
          <p className="text-xs font-bold text-slate-600 flex items-center gap-1.5"><Target className="h-3.5 w-3.5 text-red-500" />{T(`我的绩效（${data.period}）`, `My Performance (${data.period})`)}</p>
          {data.perf ? (
            <div className="mt-2">
              <div className={row}><span>{T("业绩分 / 考勤分", "Perf / attendance")}</span><b className="text-slate-700">{data.perf.perfScore} / {data.perf.attScore}</b></div>
              <div className={row}><span>{T("综合分", "Composite")}</span><b className="text-slate-800">{data.perf.composite}</b></div>
              <div className="flex flex-wrap gap-1 mt-1.5">
                {Object.entries(data.perf.kpi || {}).map(([k, v]) => (
                  <span key={k} className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-500">{k} <b className="text-slate-700">{typeof v === "number" && v > 999 ? fmtM(v, cur) : v}</b></span>
                ))}
              </div>
            </div>
          ) : <p className="mt-2 text-xs text-slate-300">{T("本期暂无绩效数据", "No performance data yet")}</p>}
        </div>
      </div>

      <div className="grid md:grid-cols-2 xl:grid-cols-4 gap-3">
        <div className={card} data-testid="hrms-me-leaves">
          <p className="text-xs font-bold text-slate-600 flex items-center gap-1.5"><Palmtree className="h-3.5 w-3.5 text-emerald-600" />{T("我的假期申请", "My Leave Requests")}</p>
          <div className="mt-2 space-y-1.5 max-h-48 overflow-y-auto">
            {data.leaves.length === 0 ? <p className="text-xs text-slate-300">{T("暂无请假记录", "No leave records")}</p> :
              data.leaves.map((l) => (
                <div key={l.id} className="flex-wrap md:flex-nowrap flex items-center justify-between text-xs">
                  <span className="text-slate-600">{(zh ? LEAVE_TYPES : LEAVE_TYPES_EN)[l.type] || l.type} {fmtD(l.startDate)}~{fmtD(l.endDate)}（{l.days}{T("天", "d")}）</span>
                  <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-semibold ${l.status === "approved" ? "bg-emerald-100 text-emerald-700" : l.status === "rejected" ? "bg-red-100 text-red-600" : "bg-amber-100 text-amber-700"}`}>{l.status === "approved" ? T("已批", "Approved") : l.status === "rejected" ? T("驳回", "Rejected") : T("待审", "Pending")}</span>
                </div>
              ))}
          </div>
          <Link to="/hrms/attendance" data-testid="hrms-me-leave-link" className="inline-block mt-2 text-[11px] text-indigo-600 font-semibold hover:underline">{T("去申请请假 →", "Request leave →")}</Link>
        </div>

        <MyDevices zh={zh} card={card} />
        <div className={card} data-testid="hrms-me-trainings">
          <p className="text-xs font-bold text-slate-600 flex items-center gap-1.5"><GraduationCap className="h-3.5 w-3.5 text-amber-600" />{T("我的培训记录", "My Trainings")}</p>
          <div className="mt-2 space-y-1.5 max-h-48 overflow-y-auto">
            {data.trainings.length === 0 ? <p className="text-xs text-slate-300">{T("暂无培训记录", "No training records")}</p> :
              data.trainings.map((t) => (
                <div key={t.id} className="text-xs">
                  <p className="text-slate-600 font-semibold">{t.courseName}</p>
                  <p className="text-[10px] text-slate-400">{T("完成", "Done")} {t.completedAt}{t.expiresAt ? T(` · 有效期至 ${t.expiresAt}`, ` · valid until ${t.expiresAt}`) : ""}{t.certNumber ? ` · ${t.certNumber}` : ""}</p>
                </div>
              ))}
          </div>
        </div>

        <div className={card} data-testid="hrms-me-projects">
          <p className="text-xs font-bold text-slate-600 flex items-center gap-1.5"><FolderKanban className="h-3.5 w-3.5 text-slate-500" />{T("我的在管项目", "My Projects")} <span className="text-slate-400 font-semibold">{data.projects.length}</span></p>
          <div className="mt-2 space-y-1.5 max-h-48 overflow-y-auto">
            {data.projects.length === 0 ? <p className="text-xs text-slate-300">{T("暂无关联项目", "No linked projects")}</p> :
              data.projects.map((pr) => (
                <p key={pr.id} className="text-xs text-slate-600 truncate">{pr.address}<span className="text-[10px] text-slate-400 ml-1">{pr.clientName}</span></p>
              ))}
          </div>
        </div>
      </div>
      <Dialog open={!!leaveDlg} onOpenChange={(o) => !o && setLeaveDlg(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>{T("快捷请假", "Quick Leave")}</DialogTitle></DialogHeader>
          {leaveDlg && (
            <div className="space-y-3 text-sm" data-testid="hrms-me-leave-dlg">
              <label className="block text-xs text-slate-500">{T("假期类型", "Type")}
                <select data-testid="hrms-me-leave-type" className={inp} value={leaveDlg.type} onChange={(e) => setLeaveDlg((d) => ({ ...d, type: e.target.value }))}>
                  {Object.keys(LEAVE_TYPES).map((k) => <option key={k} value={k}>{(zh ? LEAVE_TYPES : LEAVE_TYPES_EN)[k]}</option>)}
                </select>
              </label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <label className="text-xs text-slate-500">{T("开始", "Start")}<input data-testid="hrms-me-leave-start" type="date" className={inp} value={leaveDlg.startDate} onChange={(e) => setLeaveDlg((d) => ({ ...d, startDate: e.target.value }))} /></label>
                <label className="text-xs text-slate-500">{T("结束", "End")}<input data-testid="hrms-me-leave-end" type="date" className={inp} value={leaveDlg.endDate} onChange={(e) => setLeaveDlg((d) => ({ ...d, endDate: e.target.value }))} /></label>
              </div>
              <label className="block text-xs text-slate-500">{T("天数", "Days")}<input data-testid="hrms-me-leave-days" type="number" min="0.5" step="0.5" className={inp} value={leaveDlg.days} onChange={(e) => setLeaveDlg((d) => ({ ...d, days: e.target.value }))} /></label>
              <label className="block text-xs text-slate-500">{T("事由", "Reason")}<textarea data-testid="hrms-me-leave-reason" rows={2} className={inp} value={leaveDlg.reason} onChange={(e) => setLeaveDlg((d) => ({ ...d, reason: e.target.value }))} placeholder={T("请假原因…", "Reason…")} /></label>
              <p className="text-[10px] text-slate-400">{T(`当前年假余额 ${b.ptoBalance} 天 · 病假余额 ${b.sickBalance} 天`, `PTO balance ${b.ptoBalance}d · sick balance ${b.sickBalance}d`)}</p>
              <div className="flex-wrap md:flex-nowrap flex justify-end gap-2">
                <button onClick={() => setLeaveDlg(null)} className="text-xs rounded-full border border-slate-300 text-slate-500 font-semibold px-4 py-2">{T("取消", "Cancel")}</button>
                <button data-testid="hrms-me-leave-submit" disabled={busy} onClick={submitLeave} className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 text-xs rounded-full bg-brand-500 hover:bg-brand-600 text-white font-semibold px-4 py-2 disabled:opacity-50">{busy ? <Loader2 className="h-3.5 w-3.5 animate-spin inline" /> : T("提交申请", "Submit")}</button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* 通知相关性过滤设置（对应 HR 反馈「通知把各部门全推给所有人」）：按部门/类型订阅，屏蔽无关通知 */}
      <Dialog open={!!notifDlg} onOpenChange={(o) => !o && setNotifDlg(null)}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{T("通知相关性过滤", "Notification Filtering")}</DialogTitle></DialogHeader>
          {notifDlg && (
            <div className="space-y-3 text-sm" data-testid="hrms-notif-dlg">
              <label className="flex items-center gap-2 text-xs text-slate-600">
                <input data-testid="hrms-notif-only" type="checkbox" checked={!!notifDlg.prefs.onlyRelevant}
                  onChange={(e) => setNotifDlg((d) => ({ ...d, prefs: { ...d.prefs, onlyRelevant: e.target.checked } }))} />
                {T("只看与我相关/本部门相关的通知（屏蔽其他部门的群发通知）", "Only show notifications relevant to me / my departments (hide other departments' broadcasts)")}
              </label>
              <div>
                <p className="text-xs font-bold text-slate-600 mb-1">{T("关注的部门", "Departments of interest")}</p>
                <div className="flex flex-wrap gap-1.5">
                  {notifDlg.departments.map((dp) => {
                    const on = (notifDlg.prefs.departments || []).includes(dp.id);
                    return (
                      <button key={dp.id} data-testid={`hrms-notif-dept-${dp.id}`} onClick={() => setNotifDlg((d) => {
                        const cur = d.prefs.departments || [];
                        return { ...d, prefs: { ...d.prefs, departments: on ? cur.filter((x) => x !== dp.id) : [...cur, dp.id] } };
                      })} className={`min-h-11 min-w-11 md:min-h-0 md:min-w-0 text-[11px] px-2.5 py-1 rounded-full border font-semibold ${on ? "bg-slate-800 border-slate-800 text-white" : "bg-white border-slate-300 text-slate-600 hover:border-slate-400"}`}>
                        {zh ? dp.name : (dp.nameEn || dp.name)}
                      </button>
                    );
                  })}
                </div>
              </div>
              <div>
                <p className="text-xs font-bold text-slate-600 mb-1">{T("关注的通知类型", "Notification types")}</p>
                {(notifDlg.knownTypes || []).length === 0 ? <p className="text-[11px] text-slate-400">{T("暂无历史通知类型", "No notification history yet")}</p> : (
                  <div className="max-h-52 overflow-y-auto rounded-lg border border-slate-100 divide-y divide-slate-50">
                    {(notifDlg.knownTypes || []).map((t) => {
                      const on = (notifDlg.prefs.types || []).includes(t.type);
                      return (
                        <label key={t.type} className="flex items-center gap-2 px-2.5 py-1.5 text-[11px] text-slate-600" data-testid={`hrms-notif-type-${t.type}`}>
                          <input type="checkbox" checked={on} onChange={() => setNotifDlg((d) => {
                            const cur = d.prefs.types || [];
                            return { ...d, prefs: { ...d.prefs, types: on ? cur.filter((x) => x !== t.type) : [...cur, t.type] } };
                          })} />
                          <span className="font-semibold">{t.type}</span><span className="text-slate-400 ml-auto">{t.count}</span>
                        </label>
                      );
                    })}
                  </div>
                )}
                <p className="text-[10px] text-slate-400 mt-1">{T("说明：定向发给本人的通知始终保留；未勾选部门/类型时，该维度不做过滤。", "Notes: notifications addressed to you are always kept; an unchecked dimension imposes no filter.")}</p>
              </div>
              <div className="flex-wrap md:flex-nowrap flex justify-end gap-2">
                <button onClick={() => setNotifDlg(null)} className="text-xs rounded-full border border-slate-300 text-slate-500 font-semibold px-4 py-2">{T("取消", "Cancel")}</button>
                <button data-testid="hrms-notif-save" disabled={busy} onClick={saveNotif} className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 text-xs rounded-full bg-brand-500 hover:bg-brand-600 text-white font-semibold px-4 py-2 disabled:opacity-50">{T("保存", "Save")}</button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
